import { useEffect, useState } from 'react';
import type { ChangePasswordRequest, User } from '@mockd/shared';
import { ApiError } from '../../utils/apiClient';
import { Modal } from '../common/Modal';
import { Alert } from '../common/Alert';

interface ChangePasswordModalProps {
  isOpen: boolean;
  user: Pick<User, 'hasPassword'>;
  onSubmit: (data: ChangePasswordRequest) => Promise<void>;
  onChanged: () => void;
  /** Passwordless accounts confirm by signing in again. */
  onReauthenticate: () => void;
  onClose: () => void;
}

export function ChangePasswordModal({
  isOpen,
  user,
  onSubmit,
  onChanged,
  onReauthenticate,
  onClose,
}: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      setNeedsReauth(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNeedsReauth(false);

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSaving(true);
    try {
      await onSubmit({ newPassword, ...(user.hasPassword && { currentPassword }) });
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'REAUTH_REQUIRED') {
        setNeedsReauth(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update your password');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user.hasPassword ? 'Change password' : 'Set a password'}
      showCloseButton
    >
      <p className="text-sm text-base-content/80 mt-4">
        {user.hasPassword
          ? 'Choose a new password for signing in with your email.'
          : 'Add a password so you can also sign in with your email and password.'}{' '}
        Every other device signed in to your account will be signed out.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        {error && <Alert variant="error">{error}</Alert>}

        {needsReauth && (
          <Alert variant="warning" role="alert">
            <div className="flex flex-col items-start gap-2">
              <span>For your security, sign in again, then come back here to set your password.</span>
              <button type="button" className="btn btn-sm" onClick={onReauthenticate}>
                Sign in again
              </button>
            </div>
          </Alert>
        )}

        {user.hasPassword && (
          <div className="fieldset">
            <label htmlFor="current-password" className="fieldset-legend">
              Current password
            </label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input w-full"
              disabled={isSaving}
            />
          </div>
        )}

        <div className="fieldset">
          <label htmlFor="new-password" className="fieldset-legend">
            New password
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="input w-full"
            disabled={isSaving}
          />
        </div>

        <div className="fieldset">
          <label htmlFor="confirm-new-password" className="fieldset-legend">
            Confirm new password
          </label>
          <input
            id="confirm-new-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input w-full"
            disabled={isSaving}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost" disabled={isSaving}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSaving || !newPassword || !confirmPassword || (user.hasPassword && !currentPassword)}
          >
            {isSaving ? 'Saving…' : user.hasPassword ? 'Change password' : 'Set password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
